/**
 * WP-A (Gap B) — The governed evidence surface for one Loop.
 *
 * Everything visible here comes from the hosted authority. The component holds
 * no evidence state of its own beyond the file the person is currently choosing,
 * and it makes NO eligibility judgement: whether a record may support a
 * condition is asked of the backend predicate and reported as the backend's
 * answer, or as "not confirmed" when the authority did not answer.
 *
 * WP-A-UI — "Verify with evidence" is offered ONLY for a record the backend has
 * just confirmed eligible for a specific condition. The click is a request: the
 * existing lifecycle command (`verifyCondition`, method `evidence`) re-decides
 * eligibility in PostgreSQL, and the screen changes only after the authority
 * has been re-read. A refusal is shown as a categorical sentence and the state
 * is re-read — never overridden.
 *
 * There is deliberately no control that marks a file safe, sufficient, accepted,
 * or verified from the browser.
 */

import { useEffect, useRef, useState } from "react";
import { FileUp, Loader2, RefreshCw, ShieldAlert, ShieldCheck, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  EVIDENCE_ACCEPT_ATTRIBUTE,
  EVIDENCE_MAX_BYTES,
  evidenceProductState,
  evidenceRetryAllowed,
  evidenceStateCopy,
  useHostedEvidence,
  useHostedEvidenceActions,
} from "@/lib/evidence/product/hostedEvidence";
import type { HostedMutationResult } from "@/lib/loops/product/hostedLoops";
import type { HostedLoopCondition } from "@/lib/loops/read/loopProjection";
import { formatDate } from "@/lib/looseend/format";
import { cn } from "@/lib/utils";

export function HostedEvidencePanel({
  loopId,
  conditions,
  onVerifyWithEvidence,
  verifyBusy = false,
}: {
  loopId: string;
  conditions: readonly HostedLoopCondition[];
  /**
   * Ask the backend lifecycle authority to verify `conditionId` with the
   * evidence record `evidenceId`. Absent when the surface cannot issue lifecycle
   * commands, in which case no verification action is rendered.
   */
  onVerifyWithEvidence?:
    ((evidenceId: string, conditionId: string) => Promise<HostedMutationResult>) | undefined;
  verifyBusy?: boolean;
}) {
  const evidence = useHostedEvidence(loopId);
  const actions = useHostedEvidenceActions();
  const [notice, setNotice] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  /** The backend's own answers, keyed `evidenceId:conditionId`. Never inferred. */
  const [eligibility, setEligibility] = useState<Record<string, boolean | null>>({});
  /** The pair whose verification request is in flight, if any. */
  const [verifyingPair, setVerifyingPair] = useState<string | null>(null);
  const asking = useRef(new Set<string>());

  const unverified = conditions.filter((c) => c.verificationState !== "verified");

  async function onPick(file: File) {
    setNotice(null);
    setUploading(true);
    const result = await actions.submit(loopId, file);
    setUploading(false);
    setNotice(
      result.ok ? "Added. It will be checked before it can confirm anything." : result.message,
    );
  }

  async function onAsk(evidenceId: string, conditionId: string) {
    const key = `${evidenceId}:${conditionId}`;
    if (asking.current.has(key)) return;
    asking.current.add(key);
    try {
      const answer = await actions.supportsCondition(evidenceId, conditionId);
      setEligibility((prev) => ({ ...prev, [key]: answer }));
    } finally {
      asking.current.delete(key);
    }
  }

  // Every linked, accepted pair is asked of the backend once it appears, so the
  // verification action reflects the authority's answer rather than a guess.
  useEffect(() => {
    for (const entry of evidence.entries) {
      if (evidenceProductState(entry) !== "accepted") continue;
      for (const condition of unverified) {
        if (!entry.boundConditionIds.includes(condition.conditionId)) continue;
        const key = `${entry.record.evidenceId}:${condition.conditionId}`;
        if (key in eligibility) continue;
        void onAsk(entry.record.evidenceId, condition.conditionId);
      }
    }
    // `eligibility` and `onAsk` are intentionally omitted: the effect re-runs when
    // the authoritative entries or conditions change, and `onAsk` dedupes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidence.entries, unverified.map((c) => c.conditionId).join(",")]);

  async function onVerify(evidenceId: string, conditionId: string) {
    if (!onVerifyWithEvidence) return;
    const key = `${evidenceId}:${conditionId}`;
    setNotice(null);
    setVerifyingPair(key);
    try {
      const result = await onVerifyWithEvidence(evidenceId, conditionId);
      if (result.ok) {
        setNotice("Evidence verified. This outcome is now confirmed.");
      } else {
        // The refusal stands. Forget the earlier answer, re-read authority and
        // ask the backend again so the row reflects its current decision.
        setNotice(result.message);
        setEligibility((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        await actions.refresh(loopId);
        await onAsk(evidenceId, conditionId);
        return;
      }
    } finally {
      setVerifyingPair(null);
    }
    await actions.refresh(loopId);
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2 rounded-xl border border-border bg-surface p-3">
        <Label htmlFor="hosted-evidence-file">Add proof</Label>
        <Input
          id="hosted-evidence-file"
          type="file"
          accept={EVIDENCE_ACCEPT_ATTRIBUTE}
          disabled={uploading || actions.busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void onPick(file);
          }}
        />
        <p className="text-xs text-muted-foreground">
          A receipt, confirmation email, screenshot or document — PDF, PNG, JPEG or plain text, up
          to {Math.round(EVIDENCE_MAX_BYTES / 1_048_576)} MB. Stored privately and checked before it
          can be used.
        </p>
        {uploading ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
            Uploading and checking…
          </p>
        ) : null}
        {notice ? (
          <p className="text-xs text-foreground" role="status">
            {notice}
          </p>
        ) : null}
      </div>

      {evidence.phase === "loading" ? (
        <div className="h-20 animate-pulse rounded-xl bg-muted" aria-busy="true" />
      ) : evidence.phase === "unavailable" ? (
        <div className="space-y-2 rounded-xl border border-border p-3">
          <p className="text-sm text-foreground">{evidence.message}</p>
          <Button size="sm" variant="outline" onClick={evidence.refresh}>
            <RefreshCw className="size-4" aria-hidden="true" />
            Try again
          </Button>
        </div>
      ) : evidence.entries.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
          No evidence yet. Add something that shows progress or completion.
        </p>
      ) : (
        <ul className="space-y-2.5">
          {evidence.entries.map((entry) => {
            const state = evidenceProductState(entry);
            const copy = evidenceStateCopy(state);
            const id = entry.record.evidenceId;
            return (
              <li key={id} className="space-y-2 rounded-xl border border-border p-3">
                <div className="flex items-start gap-3">
                  {state === "accepted" ? (
                    <ShieldCheck
                      className="mt-0.5 size-4 shrink-0 text-status-resolved"
                      aria-hidden="true"
                    />
                  ) : (
                    <ShieldAlert
                      className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-medium text-foreground">{entry.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {copy.label} · {copy.detail}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Added {formatDate(entry.createdAt)}
                      {entry.record.byteSize
                        ? ` · ${Math.max(1, Math.round(entry.record.byteSize / 1024))} KB`
                        : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {evidenceRetryAllowed(entry) ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={actions.busy}
                        onClick={async () => {
                          const result = await actions.reinspect(id, loopId);
                          if (!result.ok) setNotice(result.message);
                        }}
                      >
                        <RefreshCw className="size-4" aria-hidden="true" />
                        <span className="sr-only">Re-check</span>
                      </Button>
                    ) : null}
                    {state === "accepted" ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          const result = await actions.download(id);
                          if (result.ok) window.open(result.url, "_blank", "noopener");
                          else setNotice(result.message);
                        }}
                      >
                        <FileUp className="size-4" aria-hidden="true" />
                        <span className="sr-only">Open</span>
                      </Button>
                    ) : null}
                    {state === "removed" ? null : (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={actions.busy}
                        onClick={async () => {
                          const result = await actions.remove(id, loopId);
                          if (!result.ok) setNotice(result.message);
                        }}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                        <span className="sr-only">Remove</span>
                      </Button>
                    )}
                  </div>
                </div>

                {state === "accepted" && unverified.length ? (
                  <div className="space-y-1.5 border-t border-border pt-2">
                    {unverified.map((condition) => {
                      const bound = entry.boundConditionIds.includes(condition.conditionId);
                      const key = `${id}:${condition.conditionId}`;
                      const answer = eligibility[key];
                      const isVerifying = verifyingPair === key;
                      return (
                        <div
                          key={condition.conditionId}
                          className="flex flex-wrap items-center gap-2 text-xs"
                        >
                          <span className="min-w-0 flex-1 truncate text-muted-foreground">
                            {condition.statement}
                          </span>
                          {bound ? (
                            <>
                              <span
                                className={cn(
                                  "text-muted-foreground",
                                  answer === true && "text-status-resolved",
                                )}
                                data-testid="evidence-eligibility"
                              >
                                {answer === true
                                  ? "Backend confirms it can support this"
                                  : answer === false
                                    ? "Backend says it cannot support this"
                                    : key in eligibility
                                      ? "Linked — not confirmed"
                                      : "Linked — checking…"}
                              </span>
                              {answer === true && onVerifyWithEvidence ? (
                                <Button
                                  size="sm"
                                  data-testid="evidence-verify"
                                  aria-label={`Verify “${condition.statement}” with this evidence`}
                                  aria-busy={isVerifying}
                                  disabled={
                                    isVerifying ||
                                    verifyBusy ||
                                    actions.busy ||
                                    verifyingPair !== null
                                  }
                                  onClick={() => void onVerify(id, condition.conditionId)}
                                >
                                  {isVerifying ? (
                                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                                  ) : (
                                    <ShieldCheck className="size-4" aria-hidden="true" />
                                  )}
                                  {isVerifying ? "Confirming…" : "Verify with evidence"}
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={verifyingPair !== null}
                                  onClick={() => void onAsk(id, condition.conditionId)}
                                >
                                  Check
                                </Button>
                              )}
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={actions.busy}
                              onClick={async () => {
                                const result = await actions.bindToCondition(
                                  id,
                                  condition.conditionId,
                                  loopId,
                                );
                                if (!result.ok) setNotice(result.message);
                                else await onAsk(id, condition.conditionId);
                              }}
                            >
                              Link
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
